import { Component, computed, inject, input, output, signal } from "@angular/core";
import { IScoring, ScoringComponent } from "src/app/infopage/components/scoring/scoring.component";
import { IEnergyRatingSingle, ShareAppartementInfoDto } from "src/models/estate/key-info.model";
import { ImagePackage } from "src/models/common/image.model";
import { EVEstateType } from "src/models/units/unit-attributes.model";
import { ConvertUtil } from "src/util/tools/convert.util";
import { ImageCarouselComponent } from "src/app/shared-components/image-carousel/image-carousel.component";
import { KeyValuePairListComponent, KeyValuePair } from "../../components/key-value-pair-list/key-value-pair-list.component";
import { SingleEnergyLabelComponent } from "src/app/shared-components/energy-label/single-energy-label.component";
import {
    IForeclosureDiscount,
    IUnitTotalPhysicalExposures,
    KeyInfoPhysicalExposureComponent,
} from "../../components/key-info-physical-exposure/key-info-physical-exposure.component";
import { AccordionIdentity } from "../../models/accordion-state.class";
import { SergeantTitleComponent } from "../../components/sergeant-title/sergeant-title.component";
import { KeyInfoSpecialConstraintsComponent } from "../../components/key-info-special-constraints/key-info-special-constraints.component";
import { IUnitAggregatedSpecialConstraintData } from "src/models/units/special-constraints.model";
import { KeyInfoForeclosureDiscountComponent } from "../../components/key-info-foreclosure-discount/key-info-foreclosure-discount.component";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { toSignal } from "@angular/core/rxjs-interop";
import { EvLinkTaskbarComponent } from "../../components/link-taskbar/link-taskbar.component";
import { MenuItem } from "src/services/shared/link-helper.service";
import { EnergyLabelUtil } from "src/util/tools/energy-label.utils";
import { EnergyLabelColor } from "src/models/common/energy.enums";

@Component({
    selector: "key-info",
    templateUrl: "./key-info.component.html",
    styleUrl: "./key-info.component.scss",
    imports: [
        SingleEnergyLabelComponent,
        ScoringComponent,
        EvLinkTaskbarComponent,
        ImageCarouselComponent,
        KeyValuePairListComponent,
        SingleEnergyLabelComponent,
        KeyInfoPhysicalExposureComponent,
        SergeantTitleComponent,
        KeyInfoSpecialConstraintsComponent,
        KeyInfoForeclosureDiscountComponent,
    ],
})
export class KeyInfoComponent {
    private readonly authService = inject(EvAuthorizationService);
    keyInfoData = input.required<ShareAppartementInfoDto>();
    physicalExposures = input<IUnitTotalPhysicalExposures>();
    imagePackages = input<ImagePackage[]>([]);
    specialConstraints = input<IUnitAggregatedSpecialConstraintData>(null);
    expandDetailsClicked = output<AccordionIdentity>();
    foreclosureDiscount = input<IForeclosureDiscount>(null);
    navigationMenuItems = input.required<MenuItem[]>();

    protected energyLabel = computed(() => {
        return EnergyLabelUtil.createEnergyLabelWithCssString(this.keyInfoData()?.energyLabel);
    });

    protected hasForeclosureDiscountRole = toSignal(this.authService.hasPermission(EVServiceType.Estimate_ForeclosureDiscount)); //60 - objektrisiko));
    protected hasSpecialConstraintsRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_SpecialConstraints));
    protected hasPhysicalExposuresRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_PhysicalExposure));

    protected goToPhysicalExposures() {
        this.expandDetailsClicked.emit(AccordionIdentity.PhysicalRisk);
    }
    protected goToSpecialConstraints() {
        this.expandDetailsClicked.emit(AccordionIdentity.SpecialConstraints);
    }
    protected onShowMoreKeyinfo(): void {
        this.expandDetailsClicked.emit(AccordionIdentity.KeyInfo);
    }
    protected goToEnergyInformation(): void {
        this.expandDetailsClicked.emit(AccordionIdentity.Energy);
    }
    protected propertyImageClass = computed(() => {
        const estateType = ConvertUtil.estateTypeToCssString(this.keyInfoData()?.estateType ?? EVEstateType.Annet);
        return `${estateType}`;
    });
    protected energyRating = computed<IEnergyRatingSingle>(() => {
        const energyInfo = this.keyInfoData()?.energyInfo;

        const energyRating: IEnergyRatingSingle = {
            energyLabelCertificate: energyInfo?.energyLabelCertificateMethodName,
            energyLabelCertificateDescription: energyInfo?.energyLabelCertificateMethodDescription,
            energyLabelingDate: energyInfo?.energyLabelDate,
            energyLabelingSource: energyInfo?.energyLabelSourceTypeCategoryName,
            energyLabelingColor: this.energyLabelColor(),
            energyLabelingColorEnum: energyInfo?.energyLabelColor,
            energyLabelClass: this.getEnergyLabelClass(),
            energyLabelExpired: energyInfo?.energyLabelExpired,
            energyLabel: energyInfo?.energyLabel,
            isGreenHome: energyInfo?.greenHome,
            greenHomeDescription: "Boligen har energikilde som er basert på fornybar energi",
            energyLabelVersion: energyInfo?.energyLabelVersion,
        };

        return energyRating;
    });

    energyLabelColor = computed(() => {
        switch (this.keyInfoData()?.energyLabel?.energyLabelingColor) {
            case EnergyLabelColor.Green:
                return "Grønn";
            case EnergyLabelColor.LightGreen:
                return "Lys grønn";
            case EnergyLabelColor.Yellow:
                return "Gul";
            case EnergyLabelColor.Orange:
                return "Oransje";
            case EnergyLabelColor.Red:
                return "Rød";
            default:
                return "-";
        }
    });

    getEnergyLabelClass(): string {
        return (
            this.keyInfoData()?.energyInfo?.energyLabel?.toLowerCase() + "-" + EnergyLabelColor[this.keyInfoData()?.energyInfo?.energyLabelColor]?.toLowerCase()
        );
    }

    protected score = computed(() => {
        const keyInfo = this.keyInfoData();
        const score: IScoring = {
            areaMarketScore: keyInfo?.estateType === EVEstateType.Fritidsbolig ? keyInfo?.areaVacationHomeMarketScore : keyInfo?.areaMarketScore,
            councilScore: keyInfo?.councilScore,
        };
        return score;
    });

    protected keyValuePairs = computed<KeyValuePair[]>(() => {
        const info = this.keyInfoData();
        if (!info) {
            return [];
        }
        return [
            { key: "Byggeår", value: info.buildYear },
            { key: "Etasje", value: info.floorNumber },
            { key: "Bruksenhetsnummer", value: info.functionalUnitNumber },
            { key: "Etasjer", value: info.numberOfFloors },
            { key: "Antall rom", value: info.numberOfRooms },
            { key: "BRA", value: ConvertUtil.toNumberWithPostfix(info.usableArea, "-", "m²", true) },
        ];
    });
}
